import dayjs from "dayjs"

import { DATE_FORMAT } from "@/constants/formats"
import type { BulkPriceChangeUpdateInput, PriceChangeItem, PriceChangeItemGroup } from "@/graphql/codegen/graphql"
import type { PricingGridRow } from "@/screens/Pricing/components/tables/types"
import { pricingCalc } from "@/screens/Pricing/utils/pricingCalc"
import { getPriceChangeEditKey } from "@/screens/Pricing/utils/pricingRow"
import { isNil } from "@/utils/utils"

interface BulkPreviewValues {
  newPrice: number
  newAcceptedCost: number
}

const round4 = (value: number): number => Math.round(value * 1e4) / 1e4

export const calculateBulkPreviewValues = (
  currentPrice: number,
  currentAcceptedCost: number,
  bulk: BulkPriceChangeUpdateInput
): BulkPreviewValues => {
  const currentMarginPct = currentPrice !== 0 ? ((currentPrice - currentAcceptedCost) / currentPrice) * 100 : 0

  let newAcceptedCost = currentAcceptedCost
  if (!isNil(bulk.acceptedCostChangePercent)) {
    newAcceptedCost = currentAcceptedCost * (1 + bulk.acceptedCostChangePercent / 100)
  } else if (!isNil(bulk.acceptedCostChangeDollar)) {
    newAcceptedCost = currentAcceptedCost + bulk.acceptedCostChangeDollar
  }
  newAcceptedCost = round4(newAcceptedCost)

  let newPrice = currentPrice
  if (bulk.maintainMargins && newAcceptedCost !== currentAcceptedCost && currentMarginPct < 100) {
    newPrice = newAcceptedCost / (1 - currentMarginPct / 100)
  } else if (!isNil(bulk.priceChangePercent)) {
    newPrice = currentPrice * (1 + bulk.priceChangePercent / 100)
  } else if (!isNil(bulk.priceChangeDollar)) {
    newPrice = currentPrice + bulk.priceChangeDollar
  }
  newPrice = round4(newPrice)

  return { newPrice, newAcceptedCost }
}

// One clone per PriceChangeItem — every tier row of that item must share this reference
// or AG Grid row-spanning (which compares data.item by identity) breaks.
export const toBulkPreviewItem = (item: PriceChangeItem, bulk: BulkPriceChangeUpdateInput): PriceChangeItem => ({
  ...item,
  effectiveDate: bulk.effectiveDate ?? item.effectiveDate ?? dayjs().format(DATE_FORMAT.ISO_18601),
  expirationDate: bulk.expirationDate ?? item.expirationDate ?? null,
})

// Rows already holding a persisted edit are skipped: the user's value wins.
export const getDefaultedRowKeys = (
  groups: PriceChangeItemGroup[],
  bulk: BulkPriceChangeUpdateInput,
  persistedEdits: Record<string, unknown>
): string[] => {
  if (bulk.effectiveDate) return []

  return groups.flatMap((group) =>
    group.items
      .filter((item) => !item.effectiveDate)
      .flatMap((item) => item.tiers.map((tier) => getPriceChangeEditKey(group.id, { id: tier.id, item })))
      .filter((key) => !(key in persistedEdits))
  )
}

// Call on the server-derived base row only, never on a row rehydrated from a persisted
// edit. Preserves row.item by reference.
export const toBulkPreviewRow = (row: PricingGridRow, bulk: BulkPriceChangeUpdateInput): PricingGridRow => {
  const currentPrice = row.previousPrice ?? row.price ?? 0
  const currentAcceptedCost = row.acceptedCost ?? 0
  const { newPrice, newAcceptedCost } = calculateBulkPreviewValues(currentPrice, currentAcceptedCost, bulk)

  return {
    ...row,
    acceptedCost: newAcceptedCost,
    newPrice,
    newProfit: pricingCalc.newProfit({ newPrice, acceptedCost: newAcceptedCost }),
    newMargin: pricingCalc.newMargin({ newPrice, acceptedCost: newAcceptedCost }),
  }
}
